import { useState, useEffect, useCallback, useRef } from "react";
import { Button, Skeleton, cn, HoverCard, HoverCardTrigger, HoverCardContent } from "@tellescope/ui";
import { useAuth } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { getPortalSchemaRestrictions } from "../../shared/types/sessionExtensions";
import { Code2 } from "lucide-react";
import { PortalPreview } from "../components/PortalPreview";
import { EnduserSelector } from "../components/EnduserSelector";
import { SchemaEditor } from "../components/SchemaEditor";
import { SchemaActions } from "../components/SchemaActions";
import { usePortalSchema } from "../hooks/usePortalSchema";
import { useOrganization, useRefetchOrganizationWithBranding } from "../../shared/store/organizationStore";
import {
  usePortalSchemaStore,
  usePortalSchema as useSavedSchema,
  usePortalSchemaLoading,
  useSetPortalSchema,
  usePrepareForBrandSwitch,
} from "../../shared/store/portalSchemaStore";
import { ConfigurationSelector, type ConfigurationTarget } from "../components/ConfigurationSelector";
import { usePortalBrandings } from "../hooks/usePortalBrandings";

/** Viewport modes for the preview panel */
export type PreviewViewportMode = "split" | "fullscreen" | "mobile";

/** Editor view modes */
export type EditorViewMode = "ui" | "json";

export const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { logout, currentUser, session } = useAuth();
  const [enduserSelectorOpen, setEnduserSelectorOpen] = useState(false);
  const [viewportMode, setViewportMode] = useState<PreviewViewportMode>("split");
  const [editorViewMode, setEditorViewMode] = useState<EditorViewMode>("ui");

  // Load saved schema from organization
  const organization = useOrganization();
  const fetchSavedSchema = usePortalSchemaStore((state) => state.fetchSchema);
  const prepareForBrandSwitch = usePrepareForBrandSwitch();
  const savedSchema = useSavedSchema();
  const isLoadingSchema = usePortalSchemaLoading();
  const setSavedSchema = useSetPortalSchema();

  // Fetch portal brandings for multi-configuration support
  const { brandings, isLoading: brandingsLoading } = usePortalBrandings();
  const refetchWithBranding = useRefetchOrganizationWithBranding();

  // Track selected configuration in local state so the dropdown reflects the user's intent
  // immediately, independent of what organization.brandId happens to be
  const [configTarget, setConfigTarget] = useState<ConfigurationTarget>({ type: "default" });

  // Track if initial load has been done to avoid re-fetching on mount
  const hasInitiallyLoaded = useRef(false);

  // Fetch saved schema when organization is available (initial load only)
  useEffect(() => {
    if (organization && !hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      fetchSavedSchema(organization.portalV2SchemaURL);
    }
  }, [organization, fetchSavedSchema]);

  // Handle configuration change - refetch organization info with branding's subdomain
  const handleConfigChange = useCallback(
    async (newTarget: ConfigurationTarget) => {
      setConfigTarget(newTarget);

      const subdomain =
        newTarget.type === "branding" ? brandings.find((b) => b.id === newTarget.brandingId)?.subdomain : undefined;
      const updatedOrg = await refetchWithBranding(subdomain);

      // Reset only hasAttemptedFetch (not the schema) so the current schema stays visible
      // during the fetch, then fetchSchema replaces it atomically when the new one arrives
      prepareForBrandSwitch();
      await fetchSavedSchema(updatedOrg?.portalV2SchemaURL);
    },
    [refetchWithBranding, brandings, prepareForBrandSwitch, fetchSavedSchema]
  );

  const restrictions = session ? getPortalSchemaRestrictions(session.userInfo) : undefined;
  const canEditContent = !restrictions?.disableEditContent;
  const canEditTheming = !restrictions?.disableEditTheming;
  const canEditSnippets = !restrictions?.disableEditSnippets;
  const canEdit = canEditContent || canEditTheming || canEditSnippets;
  // Import and JSON editor give full-schema access, so both require unrestricted editing
  const canImportOrEditJson = canEditContent && canEditTheming && canEditSnippets;

  // Derive the origin for the "View Live" button based on the selected branding.
  // For a branded portal, use its customPortalURL or swap its subdomain into the
  // current hostname pattern (e.g. foo.staging-portal.tellescope.com → bar.staging-portal.tellescope.com).
  // Falls back to window.location.origin for the default portal.
  const livePortalOrigin = (() => {
    if (configTarget.type !== "branding") return undefined;
    const branding = brandings.find((b) => b.id === configTarget.brandingId);
    if (!branding) return undefined;
    if (branding.customPortalURL) return branding.customPortalURL;
    if (branding.subdomain) {
      const { hostname, protocol, port } = window.location;
      const dotIndex = hostname.indexOf(".");
      const hostSuffix = dotIndex !== -1 ? hostname.slice(dotIndex) : hostname;
      const portSuffix = port ? `:${port}` : "";
      return `${protocol}//${branding.subdomain}${hostSuffix}${portSuffix}`;
    }
    return undefined;
  })();

  // Portal schema state and operations - initialized from saved schema
  const portalSchema = usePortalSchema(savedSchema);

  if (!savedSchema) {
    return (
      <div className="h-screen flex bg-background">
        {/* Left side skeleton */}
        <div className="w-1/2 border-r flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-muted border-b">
            <Skeleton className="h-5 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
          <div className="flex-1 p-4 space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
        {/* Right side skeleton */}
        <div className="w-1/2 p-4 space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background">
      {/* Left side - Admin controls (hidden in fullscreen mode) */}
      <div
        className={cn("border-r flex flex-col transition-all duration-300", {
          "w-1/2": viewportMode === "split",
          "w-0 overflow-hidden border-r-0": viewportMode === "fullscreen",
          "flex-1": viewportMode === "mobile",
        })}
      >
        {/* Admin header */}
        <div className="flex flex-wrap items-center px-4 py-2 bg-muted border-b gap-x-2 gap-y-1">
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-sm">
              <span className="text-muted-foreground">{t("Admin")}:</span>{" "}
              <HoverCard openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <span className="font-medium truncate cursor-help">{currentUser?.email}</span>
                </HoverCardTrigger>
                <HoverCardContent className="w-72" align="start">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">{t("User Details")}</h4>
                    {currentUser?.id && (
                      <div>
                        <span className="text-xs text-muted-foreground">{t("ID")}</span>
                        <p className="text-sm font-mono truncate">{currentUser?.id}</p>
                      </div>
                    )}
                    {(currentUser?.fname || currentUser?.lname) && (
                      <div>
                        <span className="text-xs text-muted-foreground">{t("Name")}</span>
                        <p className="text-sm">{[currentUser?.fname, currentUser?.lname].filter(Boolean).join(" ")}</p>
                      </div>
                    )}
                    {currentUser?.email && (
                      <div>
                        <span className="text-xs text-muted-foreground">{t("Email")}</span>
                        <p className="text-sm font-mono break-all">{currentUser?.email}</p>
                      </div>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            {brandings.length > 0 && (
              <ConfigurationSelector
                brandings={brandings}
                value={configTarget}
                onChange={handleConfigChange}
                disabled={isLoadingSchema || brandingsLoading}
              />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {canImportOrEditJson && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditorViewMode(editorViewMode === "ui" ? "json" : "ui")}
                className={cn(
                  "h-7 px-2 text-xs",
                  editorViewMode === "json" && "bg-amber-100 text-amber-700 hover:bg-amber-200"
                )}
              >
                <Code2 className="h-3.5 w-3.5 mr-1" />
                {t("JSON")}
              </Button>
            )}
            <SchemaActions
              schema={portalSchema.schema}
              savedSchema={savedSchema}
              onImport={portalSchema.importSchema}
              onSaveSuccess={setSavedSchema}
              session={session}
              configTarget={configTarget}
              canEdit={canEdit}
              canImport={canImportOrEditJson}
            />
            <Button variant="ghost" size="sm" onClick={() => logout()}>
              {t("Logout")}
            </Button>
          </div>
        </div>

        {/* Schema Editor */}
        <div className="flex-1 overflow-hidden">
          <SchemaEditor
            schema={portalSchema.schema}
            selectedPageId={portalSchema.selectedPageId}
            selectedPage={portalSchema.selectedPage}
            setSelectedPageId={portalSchema.setSelectedPageId}
            addSnippet={portalSchema.addSnippet}
            updateSnippet={portalSchema.updateSnippet}
            removeSnippet={portalSchema.removeSnippet}
            addPage={portalSchema.addPage}
            updatePage={portalSchema.updatePage}
            removePage={portalSchema.removePage}
            reorderPages={portalSchema.reorderPages}
            addBlock={portalSchema.addBlock}
            updateBlock={portalSchema.updateBlock}
            removeBlock={portalSchema.removeBlock}
            reorderBlocks={portalSchema.reorderBlocks}
            updateSettings={portalSchema.updateSettings}
            importSchema={portalSchema.importSchema}
            viewMode={editorViewMode}
            onViewModeChange={setEditorViewMode}
            canEditContent={canEditContent}
            canEditTheming={canEditTheming}
            canEditSnippets={canEditSnippets}
          />
        </div>
      </div>

      {/* Right side - Portal preview */}
      <div
        className={cn("overflow-hidden flex justify-center bg-muted/30 transition-all duration-300", {
          "w-1/2": viewportMode === "split",
          "w-full": viewportMode === "fullscreen",
          "w-[375px] flex-shrink-0": viewportMode === "mobile",
        })}
      >
        <div
          className={cn("h-full bg-background", {
            "w-full": viewportMode !== "mobile",
            "w-full border-x shadow-lg": viewportMode === "mobile",
          })}
        >
          <PortalPreview
            schema={portalSchema.schema}
            onSelectEnduser={() => setEnduserSelectorOpen(true)}
            selectedPageId={portalSchema.selectedPageId}
            viewportMode={viewportMode}
            onViewportModeChange={setViewportMode}
            livePortalOrigin={livePortalOrigin}
          />
        </div>
      </div>

      {/* Enduser selector dialog */}
      <EnduserSelector open={enduserSelectorOpen} onOpenChange={setEnduserSelectorOpen} />
    </div>
  );
};
